import { getDetailLabel } from '@/shared/copy/details';
import { type GridColDef } from '@/ui/primitives';
import type { TestListItemDto } from '@flowqa/shared';

type BuildHiddenFilterColumnsArgs = {
  singleSelectOperators: GridColDef<TestListItemDto>['filterOperators'];
  numericOperators: GridColDef<TestListItemDto>['filterOperators'];
  dateOperators: GridColDef<TestListItemDto>['filterOperators'];
  filterSuites: Array<{ value: string; label: string }>;
  filterTags: string[];
};

type HiddenFilterColumns = {
  suiteFilterColumn: GridColDef<TestListItemDto>;
  tagsFilterColumn: GridColDef<TestListItemDto>;
  durationFilterColumn: GridColDef<TestListItemDto>;
  timestampFilterColumn: GridColDef<TestListItemDto>;
  statusFilterColumn: GridColDef<TestListItemDto>;
};

export const buildHiddenFilterColumns = ({
  singleSelectOperators,
  numericOperators,
  dateOperators,
  filterSuites,
  filterTags,
}: BuildHiddenFilterColumnsArgs): HiddenFilterColumns => {
  const suiteFilterColumn: GridColDef<TestListItemDto> = {
    field: 'suiteId',
    headerName: 'Suite',
    type: 'singleSelect',
    valueOptions: filterSuites,
    filterOperators: singleSelectOperators,
    filterable: true,
    sortable: false,
    hideable: false,
    disableColumnMenu: true,
    minWidth: 1,
    width: 1,
    flex: 0,
    valueGetter: (_value, row) => row.suites?.primaryId ?? row.suites?.ids?.[0] ?? null,
    renderCell: () => null,
  };

  const tagsFilterColumn: GridColDef<TestListItemDto> = {
    field: 'tags',
    headerName: 'Tags',
    type: 'singleSelect',
    valueOptions: filterTags,
    filterOperators: singleSelectOperators,
    filterable: true,
    sortable: false,
    hideable: false,
    disableColumnMenu: true,
    minWidth: 1,
    width: 1,
    flex: 0,
    valueGetter: (_value, row) => row.tags?.[0] ?? null,
    renderCell: () => null,
  };

  const durationFilterColumn: GridColDef<TestListItemDto> = {
    field: 'latestRunDurationMs',
    headerName: getDetailLabel('duration'),
    type: 'number',
    filterOperators: numericOperators,
    filterable: true,
    sortable: false,
    hideable: false,
    disableColumnMenu: true,
    minWidth: 1,
    width: 1,
    flex: 0,
    valueGetter: (_value, row) => row.latestRun?.durationMs ?? null,
    renderCell: () => null,
  };

  const timestampFilterColumn: GridColDef<TestListItemDto> = {
    field: 'latestRunTimestamp',
    headerName: getDetailLabel('timestamp'),
    type: 'dateTime',
    filterOperators: dateOperators,
    filterable: true,
    sortable: false,
    hideable: false,
    disableColumnMenu: true,
    minWidth: 1,
    width: 1,
    flex: 0,
    valueGetter: (_value, row) => row.latestRun?.timestamp ?? null,
    renderCell: () => null,
  };

  const statusFilterColumn: GridColDef<TestListItemDto> = {
    field: 'statusFilter',
    headerName: getDetailLabel('status'),
    type: 'singleSelect',
    valueOptions: ['passed', 'failed', 'errored', 'skipped', 'untested'],
    filterOperators: singleSelectOperators,
    filterable: true,
    sortable: false,
    hideable: false,
    disableColumnMenu: true,
    minWidth: 1,
    width: 1,
    flex: 0,
    valueGetter: (_value, row) => row.latestRun?.status ?? null,
    renderCell: () => null,
  };

  return {
    suiteFilterColumn,
    tagsFilterColumn,
    durationFilterColumn,
    timestampFilterColumn,
    statusFilterColumn,
  };
};
